import React from 'react';
import { motion } from 'framer-motion';

// Bace image paths
const baceTheProblem = '/images/bace-the-problem.png';
const baceBeatboxer = '/images/bace-beatboxer.png';
const baceVoiceRecordedMidi = '/images/bace-voice-recorded-midi-bace.gif';
const bacePrototype = '/images/bace-prototype.png';
const baceVoiceTrainerFeature = '/images/bace-voice-trainer-feature.png';
const baceTrainModel = '/images/bace-train-model.png';
const baceSoftware = '/images/bace-software.png';
const baceAppVoiceTrainer = '/images/bace-app-voice-trainer.png';
const baceAppSettings = '/images/bace-app-settings.png';
const baceLogoMarkOrange = '/images/bace-logo-mark-orange.png';
const baceLogoMarkWhite = '/images/bace-logo-mark-white.png';
const baceLogo = '/images/bace-logo.png';
const baceAppIcon = '/images/bace-app-icon.png';
const baceHomeMobile1 = '/images/bace-home-mobile-1.png';
const baceHomeMobile2 = '/images/bace-home-mobile-2.png';
const baceHomeMobile3 = '/images/bace-home-mobile-3.png';
const baceHow = '/images/bace-how.png';
const baceCheckout = '/images/bace-checkout.png';
const baceCheckoutMobile = '/images/bace-checkout-mobile.png';
const baceAccountOverviewMobile = '/images/bace-account-overview-mobile.png';
const baceAccountSoftwareMobile = '/images/bace-account-software-mobile.png';
const baceAccountOverview = '/images/bace-account-overview.png';
const baceAccountSoftwareDownload = '/images/bace-account-software-download.png';
const baceMLModel = '/images/bace-ml-model.png';

const Bace: React.FC = () => {
  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="mx-auto max-w-8xl flex flex-1 flex-col gap-2 p-4 md:p-6">
        {/* Hero Section */}
        <section className="grid md:grid-cols-12 my-4 md:my-8">
          <div className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-6 md:space-y-12">
            <motion.h1
              className="md:!leading-tight text-3xl md:text-4xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              Bace's patented voice-to-MIDI software achieved 95% accuracy and is used by 450+ musicians
            </motion.h1>
            
            <motion.div
              className="grid md:grid-cols-12 md:gap-6 space-y-4 md:space-y-0 col-span-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            >
              <div className="md:col-span-5 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Team</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">
                    2x DSP Engineers<span className="mx-2">·</span>2x Software Engineers
                  </p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Tools & Frameworks</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">
                    Figma<span className="mx-1"> · </span>Axure RP<span className="mx-1"> · </span>Origami Studio<span className="mx-1"> · </span>TailwindCSS
                  </p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Date</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">2022 - Present</p>
                </div>
              </div>
              
              <div className="md:col-span-7 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Role & Contributions</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">
                    Founder / Product Designer<span className="mx-1"> · </span>User Research<span className="mx-1"> · </span>Prototyping<span className="mx-1"> · </span>Front-end Development
                  </p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Key Achievements</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">
                    Over 450+ Active Users. Patent awarded voice-to-MIDI software, achieving 95% accuracy and 0.30-second latency while leading all aspects of operations and product design.
                  </p>
                </div>
                <div className="inline-flex mt-4">
                  <a 
                    href="https://bace.app" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono-recursive md:text-lg transition text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-4 cursor-pointer"
                  >
                    View Website
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Video Section */}
        <motion.section
          className="mt-8 md:mt-14"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="mx-auto">
            <video 
              className="z-10 overflow-hidden rounded-lg md:rounded-2xl relative w-full" 
              autoPlay 
              loop 
              muted 
              playsInline
            >
              <source src="/video/Bace-app.webm" type="video/webm" />
              <source src="/video/bace-app.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              Bace app in action
            </figcaption>
          </div>
        </motion.section>

        {/* Project Overview */}
        <div className="grid md:grid-cols-12 my-12 md:my-28 space-y-12 md:space-y-28">
          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">Project Overview</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              As a product designer passionate about both technology and music, my journey with Bace started in an unexpected place: on tour with my band. Like many musicians, I'd often come up with beat ideas on the road, only to lose them before I had a chance to translate them to my DAW (digital audio workstation). Recording these ideas vocally was easy, but actually turning them into MIDI required a tedious process once I was back in the studio. That's where Bace came in a voice-to-MIDI software that converts beatboxing into MIDI notes in real-time. Now, producers can capture beat ideas anywhere, instantly.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Bace started as an ambitious dream and has since evolved into a powerful tool for musicians and creators alike. It's an audio plugin and desktop app that combines machine learning (ML) and digital signal processing (DSP) to translate beatboxing into accurate MIDI data, allowing musicians to get creative with their voice alone.
            </p>
          </motion.div>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceTheProblem} 
              alt="No easy way to translate voice ideas"
            />
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              No easy way to translate voice ideas
            </figcaption>
          </motion.figure>

          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">Problem</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              The core problem Bace addresses is that producers, beatmakers, and musicians often have creative ideas that come to them at inconvenient times—while walking, driving, or in any situation where they can't access their studio equipment. These fleeting moments of inspiration are frequently lost because there's no easy way to capture and translate vocal beat ideas into usable MIDI data.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Traditional methods require complex setups, expensive equipment, or time-consuming manual transcription. This creates a barrier between inspiration and creation, leading to lost ideas and frustrated artists who want to capture their creativity in the moment.
            </p>
          </motion.div>

          {/* Solution Images */}
          <motion.div
            className="col-span-12 grid md:grid-cols-12 gap-6 my-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="md:col-span-2" />
            <div className="md:col-span-2 place-content-center overflow-hidden rounded-lg md:rounded-2xl">
              <figure className="overflow-hidden rounded-lg md:rounded-2xl max-w-80">
                <img className="object-cover w-full" src={baceBeatboxer} alt="Beatboxer using Bace" />
              </figure>
            </div>
            <div className="md:col-span-6">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img 
                  className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                  src={baceVoiceRecordedMidi} 
                  alt="Voice to MIDI conversion in action"
                />
              </figure>
            </div>
            <div className="md:col-span-2" />
          </motion.div>

          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">The Solution: Bace's Core Features</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Bace solves this problem through a combination of advanced machine learning algorithms and intuitive user experience design. The app listens to your beatboxing in real-time and instantly converts it into MIDI notes that can be used in any digital audio workstation.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Key features include real-time voice-to-MIDI conversion, customizable sensitivity settings, multiple output formats, and seamless integration with popular DAWs. The interface is designed to be so simple that musicians can start creating within seconds of opening the app.
            </p>
          </motion.div>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={bacePrototype} 
              alt="Bace prototype interface"
            />
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              MATLAB-based prototype for voice-to-MIDI detection
            </figcaption>
          </motion.figure>

          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">Key Challenges</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              The biggest challenge was achieving the perfect balance between accuracy and speed. Users needed the conversion to be both precise enough to capture the nuances of their beatboxing and fast enough to feel natural and responsive. This required extensive testing and iteration with real musicians to understand their creative workflows.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Another significant challenge was designing an interface that could accommodate the wide range of musical styles and skill levels among our users, from professional producers to casual beatboxers just starting out.
            </p>
            </motion.div>

          {/* Technical Implementation Images */}
          <motion.div
            className="col-span-12 grid md:grid-cols-12 gap-6 my-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="md:col-span-6">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img src={baceVoiceTrainerFeature} alt="Voice trainer feature" className="w-full" />
              </figure>
            </div>
            <div className="md:col-span-6">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img src={baceTrainModel} alt="Model training interface" className="w-full" />
              </figure>
            </div>
          </motion.div>

          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">Overcoming Technical Hurdles</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              The technical implementation required deep collaboration between our DSP engineers and machine learning specialists. We developed custom algorithms that could distinguish between different beatboxing sounds (kicks, snares, hi-hats) and map them to appropriate MIDI notes and velocities.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              We also had to optimize the app's performance to ensure it could run smoothly on a wide range of devices while maintaining the low latency that musicians require for real-time performance.
            </p>
          </motion.div>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img src={baceMLModel} alt="Machine learning model" className="w-full" />
          </motion.figure>

          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">The Result</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Bace has achieved remarkable success with over 450+ active users and a patent for its voice-to-MIDI technology. The app delivers 95% accuracy in beatboxing recognition with just 0.30-second latency, making it one of the fastest and most accurate tools of its kind.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Users consistently report that Bace has transformed their creative process, allowing them to capture ideas they would have otherwise lost and experiment with new musical concepts in ways they never thought possible.
            </p>
            </motion.div>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceSoftware} 
              alt="Bace software interface"
            />
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              Final software interface
            </figcaption>
          </motion.figure>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceAppVoiceTrainer} 
              alt="Voice trainer app interface"
            />
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              Voice trainer app interface
            </figcaption>
          </motion.figure>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceAppSettings} 
              alt="App settings interface"
            />
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              App settings interface
            </figcaption>
          </motion.figure>

          {/* Branding Images */}
          <motion.div
            className="col-span-12 grid md:grid-cols-12 gap-6 my-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="md:col-span-6">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img src={baceLogoMarkOrange} alt="Bace logo mark orange" className="w-full" />
              </figure>
            </div>
            <div className="md:col-span-6">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img src={baceLogoMarkWhite} alt="Bace logo mark white" className="w-full" />
              </figure>
            </div>
            <div className="md:col-span-12">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img src={baceLogo} alt="Bace full logo" className="w-full" />
              </figure>
            </div>
            <div className="md:col-span-12">
              <figure className="bg-zinc-100 dark:bg-zinc-800 overflow-hidden rounded-lg md:rounded-2xl">
                <img src={baceAppIcon} alt="Bace app icon" className="w-full" />
              </figure>
            </div>
          </motion.div>

          {/* Mobile App Images */}
          <motion.div
            className="col-span-12 grid grid-cols-3 gap-12 md:gap-28 my-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <figure>
              <img 
                className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                src={baceHomeMobile1} 
                alt="Bace mobile home screen 1"
              />
            </figure>
            <figure>
              <img 
                className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                src={baceHomeMobile2} 
                alt="Bace mobile home screen 2"
              />
            </figure>
            <figure>
              <img 
                className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                src={baceHomeMobile3} 
                alt="Bace mobile home screen 3"
              />
            </figure>
          </motion.div>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceHow} 
              alt="How Bace works"
            />
          </motion.figure>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceCheckout} 
              alt="Bace checkout process"
            />
          </motion.figure>

          {/* Mobile Checkout Images */}
          <motion.div
            className="col-span-12 grid grid-cols-3 gap-12 md:gap-28 my-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <figure>
              <img 
                className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                src={baceCheckoutMobile} 
                alt="Bace mobile checkout"
              />
            </figure>
            <figure>
              <img 
                className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                src={baceAccountOverviewMobile} 
                alt="Bace mobile account overview"
              />
            </figure>
            <figure>
              <img 
                className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
                src={baceAccountSoftwareMobile} 
                alt="Bace mobile software account"
              />
            </figure>
          </motion.div>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceAccountOverview} 
              alt="Bace account overview"
            />
          </motion.figure>

          <motion.figure
            className="col-span-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img 
              className="overflow-hidden rounded-lg md:rounded-2xl w-full" 
              src={baceAccountSoftwareDownload} 
              alt="Bace software download"
            />
          </motion.figure>

          <motion.div
            className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-xl md:text-2xl font-medium">Conclusion</h2>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              Bace represents the perfect intersection of my passions for design, technology, and music. It's a testament to how thoughtful user research, iterative design, and cutting-edge technology can come together to solve real problems for creative professionals.
            </p>
            <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">
              The project has taught me invaluable lessons about leading cross-functional teams, managing complex technical requirements, and designing for highly specialized user needs. Most importantly, it's shown me the power of designing tools that amplify human creativity rather than replacing it.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Bace;
